import axios from 'axios';
import buildURL from '../helpers/buildURL';
import buildFullPath from '../core/buildFullPath';
import settle from '../core/settle';

/**
 * axios适配器 - 用于在非uni-app环境中使用luch-request
 *
 * 此适配器将luch-request的请求配置转换为axios格式，并将axios的响应转换回luch-request期望的格式
 *
 * @param {Object} config - luch-request的请求配置
 * @return {Promise} 返回处理结果的Promise
 *
 * @example
 * // 在luch-request实例中使用
 * import Request from 'luch-request'
 * import axiosAdapter from './adapters/axios'
 *
 * const http = new Request({
 *   baseURL: 'https://api.example.com',
 *   adapter: axiosAdapter // 使用axios适配器替代默认的uni适配器
 * })
 */
export default (config) => {
  return new Promise((resolve, reject) => {
    // 构建完整URL路径
    const fullPath = buildURL(
      buildFullPath(config.baseURL, config.url),
      config.params,
      config.paramsSerializer
    );

    const controller = new AbortController();
    // 创建axios请求配置
    const axiosConfig = {
      url: fullPath,
      method: config.method ? config.method.toLowerCase() : 'get',
      headers: config.header || {},
      timeout: config.timeout,
      responseType: config.responseType,
      withCredentials: config.withCredentials,
      signal: controller.signal
    };

    // 处理请求数据
    if (config.data !== undefined) {
      axiosConfig.data = config.data;
    }

    // 特殊处理上传和下载请求
    if (config.method === 'UPLOAD') {
      // 这里可以实现文件上传的兼容逻辑，但需要额外处理FormData
      const formData = new FormData();
      if (config.formData) {
        Object.keys(config.formData).forEach((key) => {
          formData.append(key, config.formData[key]);
        });
      }
      if (config.filePath) {
        formData.append(config.name || 'file', config.file || config.filePath);
      }
      axiosConfig.data = formData;
      axiosConfig.headers['Content-Type'] = 'multipart/form-data';
    } else if (config.method === 'DOWNLOAD') {
      // 这里可以实现文件下载的兼容逻辑，但需要额外处理文件保存
      axiosConfig.responseType = 'blob';
      axiosConfig.onDownloadProgress = (progressEvent) => {
        if (config.onDownloadProgress && progressEvent.lengthComputable) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          config.onDownloadProgress({
            progress: percentCompleted,
            totalBytesWritten: progressEvent.loaded,
            totalBytesExpectedToWrite: progressEvent.total
          });
        }
      };
    }

    // 发送请求
    const requestTask = axios(axiosConfig)
      .then((response) => {
        // 转换响应格式以匹配uni.request的响应格式
        const _response = {
          data: response.data,
          statusCode: response.status,
          header: response.headers,
          config: config,
          cookies: [],
          errMsg: 'request:ok',
          rawData: response.data
        };

        // 处理JSON解析
        try {
          let jsonParseHandle = false;
          const forcedJSONParsingType = typeof config.forcedJSONParsing;
          if (forcedJSONParsingType === 'boolean') {
            jsonParseHandle = config.forcedJSONParsing;
          } else if (forcedJSONParsingType === 'object') {
            const includesMethod = config.forcedJSONParsing.include || [];
            jsonParseHandle = includesMethod.includes(config.method);
          }

          // 对可能字符串不是json的情况容错
          if (jsonParseHandle && typeof _response.data === 'string') {
            _response.data = JSON.parse(_response.data);
          }
          if (config.method === 'DOWNLOAD' && response.data instanceof Blob) {
            /**
             * 自动触发浏览器下载
             * 1. 优先从 Content-Disposition 响应头获取文件名
             * 2. 若无则从 config.filename 或 URL 推断
             * 3. 兼容 Chrome/Edge/Firefox
             */
            let filename = '';
            const disposition = _response.header['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
              }
            }
            if (!filename) {
              filename =
                config.filename || fullPath.split('/').pop() || 'download';
            }
            if (
              typeof window !== 'undefined' &&
              window.URL &&
              window.URL.createObjectURL
            ) {
              const url = window.URL.createObjectURL(response.data);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              // 返回临时文件路径，兼容 uni-app 语义
              _response.tempFilePath = url;
            }
          }
        } catch (e) {
          // 解析错误时保持原始数据
        }

        // 使用settle处理响应
        settle(resolve, reject, _response);
      })
      .catch((error) => {
        // 处理错误响应
        let response = {};
        if (error.response) {
          // 服务器返回了错误状态码
          response = {
            data: error.response.data,
            statusCode: error.response.status,
            header: error.response.headers,
            config: config,
            errMsg: `request:fail status code ${error.response.status}`,
            rawData: error.response.data
          };
        } else if (error.request) {
          // 请求已发送但没有收到响应
          response = {
            data: null,
            statusCode: 0,
            config: config,
            errMsg: 'request:fail no response'
          };
        } else {
          // 请求设置时发生错误
          response = {
            data: null,
            statusCode: 0,
            config: config,
            errMsg: `request:fail ${error.message}`
          };
        }

        // 使用settle处理错误响应
        settle(resolve, reject, response);
      });

    // 支持取消请求
    if (config.getTask) {
      const task = {
        abort: () => {
          controller.abort();
        }
      };
      config.getTask(task, config);
    }
  });
};
